<template>
    
    <!-- Loading indicator -->
    <div v-if="isLoading" class="flex justify-center items-center h-screen">
    <div class="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
    </div>
    <div v-if="!isLoading" class="bg-blue-50">
    <div class="header-footer" >
        <HeaderComponent />
    </div>
    <div v-if="showErrorMessage" style="font-size: 1.85rem;">
        <div class="flex flex-col justify-center items-center" style="height: 64.5vh;">
        <!--<i class="fa-duotone fa-solid fa-triangle-exclamation text-5xl text-red-700"></i>-->
        <img src="@/assets/error_icon.svg" alt="signature" width="90px;" height="55px;">
        <span>{{ errorMessage }}</span> 
        </div>
    </div>
    <div v-if="!hasContractId" class="m-10">
      <div class="flex flex-col md:flex-row gap-10">
      <!-- Details Section -->
        <div class="md:w-1/2 bg-blue-50 rounded-lg order-2 md:order-1 p-6">
          <h1 class="text-3xl font-bold mb-4 text-blue-800 text-center">Salesforce e-Signature Integration Demo</h1><br>
          <h2 class="text-2xl font-semibold mb-4 text-blue-700">Transform the Way You Collect Signatures</h2>
          
          <p class="mb-4">
            Welcome to our interactive <strong>Salesforce e-Sign Demo</strong>, designed to showcase an intuitive and user-friendly way to collect electronic signatures. Whether for contracts, approvals, or any form-based workflow, this solution seamlessly integrates with Salesforce to streamline the signing process and enhance user experience.
          </p>
          
          <h3 class="text-xl font-semibold mb-3 text-blue-700">Key Features</h3>
          <ul class="list-disc pl-5 mb-4 space-y-2">
            <li>
              <strong>Effortless Signing Experience</strong> Users can <strong>draw</strong> or <strong>type</strong> their signatures directly into the form, ensuring a smooth and convenient signing process.
            </li>
            <li>
              <strong>Real-Time Salesforce Integration</strong> Upon form submission, all user inputs and signatures are integrated with Salesforce in real time. While this demo showcases the client-side experience, we can fully configure the integration to meet your business needs.
            </li>
            <li>
              <strong>Customizable Design and Branding</strong> Your forms, your way! Every aspect of the signing interface—from logos, colors, and fonts to layout and workflows—can be tailored to match your company's branding and requirements.
            </li>
            <li>
              <strong>Enhanced User Experience</strong> We prioritize simplicity and speed. Users can easily interact with the form, ensuring minimal friction and maximum efficiency.
            </li>
          </ul>
          
          <h3 class="text-xl font-semibold mb-3 text-blue-700">Why Choose Our Solution?</h3>
          <ul class="list-disc pl-5 mb-4 space-y-2">
            <li><strong>Seamless Integration with Salesforce</strong> Save time by reducing manual processes and ensuring data consistency in Salesforce.</li>
            <li><strong>Flexible and Scalable</strong> Perfect for businesses of all sizes, our solution can scale to support growing workflows and volumes.</li>
            <li><strong>Compliance and Security</strong> Maintain trust with secure and compliant electronic signatures.</li>
            <li><strong>Completely Tailored</strong> Have a unique requirement? We're here to customize workflows, branding, and functionality to fit your goals.</li>
          </ul>
          
          <h3 class="text-xl font-semibold mb-3 text-blue-700">Ready to See It in Action?</h3>
          <p class="mb-4">
            Click <strong>Start Demo</strong> to experience a simple, yet powerful, e-signature process. Explore how you can transform form-based workflows while staying connected with Salesforce—faster and smarter.
          </p>
          
          <h4 class="font-bold text-blue-800 text-center">Let's make signatures effortless!</h4>
        </div>
        <div class="md:w-1/2 order-1 md:order-2 p-6">
          <!--<DemoForm leadType="contract"/>-->
          <client-only>
              <DemoForm  leadType="contract" :pageTitle="pageTitle" :pageDescription:="pageDescription" :currentUrl:="currentUrl"/>
          </client-only>
        </div>
      </div>
    </div>
    <client-only>
        <div v-if="showContent && hasContractId">
            <div class="page" id="app">
            <h1 class="text-4xl font-bold mb-4 text-center" >Demo Contract</h1>

            <section class="mb-6">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                <h2 class="text-xl font-semibold mb-2" style="margin: 0;">SERVICE AGREEMENT</h2>
                <div v-if="errorMessages.signDate" class="error-message" style="margin: 525px;">{{ errorMessages.signDate }}</div>
                </div>
                <p>
                <strong>This Service Agreement </strong>("Agreement") is made and entered into on this&nbsp;
                <input type='date' v-if="contractData" v-model='contractData.eSignatureWrapper.signDate' placeholder="Select date"/>
                    <!--<VueDatePicker style="width: 100px;" v-if="contractData" v-model="contractData.signDate" placeholder="Select Date" :enable-time-picker="false"
                                    model-type="M/dd/yyyy" auto-apply></VueDatePicker>-->
                &nbsp;by and between:
                </p>
            </section>

            <section class="mb-6">
                <h2 class="text-xl font-semibold mb-2" v-if="contractData">{{ contractData.leadDetail.firstName }} {{ contractData.leadDetail.lastName }}</h2>
                <p>Email: <strong v-if="contractData">{{ contractData.leadDetail.email }}</strong></p>
                <p><strong>("Client")</strong></p>
            </section>

            <section class="mb-6">
                <h2 class="text-xl font-semibold mb-2">Jane Smith</h2>
                <p>123 Business Avenue, Suite 456</p>
                <p>New York, NY 10001</p>
                <p>Email: <strong>janesmith@serviceprovider.com</strong></p>
                <p><strong>("Service Provider")</strong></p>
            </section>

            <section class="mb-6">
                <h2 class="text-xl font-semibold mb-2">1. SERVICES PROVIDED</h2>
                <p>The Service Provider agrees to provide the following services to the Client:</p>
                <ul class="list-disc ml-6">
                <li>Development and design of a responsive company website.</li>
                <li>Monthly maintenance and updates for a period of 6 months post-launch.</li>
                <li>SEO optimization and integration of Google Analytics.</li>
                </ul>
            </section>

            <section class="mb-6">
                <h2 class="text-xl font-semibold mb-2">2. PAYMENT TERMS</h2>
                <p>
                <strong>a) Compensation</strong>: The Client agrees to pay the Service Provider a total fee of $10,000 for the services provided under this Agreement.
                </p>
                <p>
                <strong>b) Payment Schedule</strong>: The payment will be made as follows:
                </p>
                <ul class="list-disc ml-6">
                <li>50% ($5,000) upon signing this Agreement.</li>
                <li>25% ($2,500) upon completion of the website design.</li>
                <li>25% ($2,500) upon final delivery and launch of the website.</li>
                </ul>
                <p>
                <strong>c) Late Payment</strong>: If any payment is not received by the due date, the Service Provider reserves the right to charge a late fee of $50 per day until payment is made in full.
                </p>
            </section>

            <section class="mb-6">
                <h2 class="text-xl font-semibold mb-2">3. TERM AND TERMINATION</h2>
                <p>
                <strong>a) Term</strong>: This Agreement will commence on October 1, 2024, and will continue until March 31, 2025, or until the services have been completed, whichever comes first.
                </p>
                <p>
                <strong>b) Termination for Convenience</strong>: Either party may terminate this Agreement at any time by providing 30 days’ written notice to the other party.
                </p>
                <p>
                <strong>c) Termination for Cause</strong>: Either party may terminate this Agreement immediately if the other party breaches any material term of this Agreement and fails to remedy the breach within 14 days after receiving written notice of the breach.
                </p>
            </section>

            <section class="mb-6">
                <h2 class="text-xl font-semibold mb-2">4. CONFIDENTIALITY</h2>
                <p>
                The Service Provider agrees to maintain the confidentiality of all confidential information disclosed by the Client and will not disclose such information to any third party without the Client’s prior written consent.
                </p>
            </section>

            <section class="mb-6">
                <h2 class="text-xl font-semibold mb-2">5. INTELLECTUAL PROPERTY</h2>
                <p>
                <strong>a) Ownership</strong>: All intellectual property rights, including copyrights and trademarks, created in the course of providing the services, will be owned by the Client, subject to full payment of the service fees.
                </p>
                <p>
                <strong>b) License</strong>: The Service Provider grants the Client a non-exclusive, perpetual license to use any pre-existing intellectual property included in the deliverables provided under this Agreement.
                </p>
            </section>

            <section class="mb-6">
                <h2 class="text-xl font-semibold mb-2">6. LIABILITY AND INDEMNIFICATION</h2>
                <p>
                <strong>a) Liability</strong>: The Service Provider will not be liable for any indirect, incidental, or consequential damages arising out of or related to this Agreement.
                </p>
                <p>
                <strong>b) Indemnification</strong>: The Client agrees to indemnify and hold the Service Provider harmless from any claims, losses, damages, or expenses arising out of the Client’s use of the services provided under this Agreement.
                </p>
            </section>

            <section class="mb-6">
                <h2 class="text-xl font-semibold mb-2">7. GOVERNING LAW</h2>
                <p>
                This Agreement will be governed by and construed in accordance with the laws of the State of New York, without regard to its conflicts of law principles.
                </p>
            </section>

            <section class="mb-6">
                <h2 class="text-xl font-semibold mb-2">8. DISPUTE RESOLUTION</h2>
                <p>
                In the event of any dispute arising under this Agreement, the parties agree to first attempt to resolve the dispute through mediation. If mediation is unsuccessful, the dispute will be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
                </p>
            </section>

            <section class="mb-6">
                <h2 class="text-xl font-semibold mb-2">9. ENTIRE AGREEMENT</h2>
                <p>
                This Agreement constitutes the entire agreement between the parties and supersedes all prior agreements, understandings, and negotiations, whether written or oral, relating to its subject matter.
                </p>
            </section>

            <section class="mb-6">
                <h2 class="text-xl font-semibold mb-2">10. AMENDMENTS</h2>
                <p>
                This Agreement may be amended or modified only by a written document signed by both parties.
                </p>
            </section>

            <section class="mb-6">
                <h2 class="text-xl font-semibold mb-2">11. SIGNATURES</h2>
                <p>By signing below, the parties agree to the terms and conditions outlined in this Agreement.</p>
                <div class="mt-4">
                <p><strong v-if="contractData">{{ contractData.leadDetail.firstName }} {{ contractData.leadDetail.lastName }}</strong></p>
                <p>Client</p>
                <table style="width: 80%;">
                  <tbody>
                    <tr>
                        <td style="width: 10%; vertical-align: bottom;">
                        </td>
                        <td style="width:10%; vertical-align: bottom;">
                        <div v-if="errorMessages.place" class="error-message">{{ errorMessages.place }}</div>
                        </td>
                        <td style="width: 5%;"></td>
                        <td style="width: 5%; vertical-align: bottom;"></td>
                        <td style=" width:50%;">
                        <div v-if="errorMessages.signature" class="error-message">{{ errorMessages.signature }}</div>
                        </td>
                    </tr>
                    <tr>
                        <td style="width: 10%; vertical-align: bottom;">
                            Place : 
                        </td>
                        <td style="border-bottom: 2px dotted #333; width:10%; vertical-align: bottom;">
                        <input type='text' v-if="contractData" v-model='contractData.eSignatureWrapper.place' placeholder="Add location"/>
                        </td>
                        <td style="width: 5%;"></td>
                        <td style="width: 15%; vertical-align: bottom;">
                            Signature : 
                        </td>
                        <td style="border-bottom: 2px dotted #333; width:50%;">
                            <SignatureComponent v-if="contractData" v-model="contractData.eSignatureWrapper.signData"/>
                        </td>
                    </tr>
                    <tr>
                        <td style="width: 10%; padding-top: 15px;">
                            Date : 
                        </td>
                        <td style="width:10%;vertical-align: bottom;">
                            <strong v-if="contractData">{{ contractData.eSignatureWrapper.signDate }}</strong>
                        </td>
                    </tr>
                  </tbody>
                </table>
                </div>
            </section>
            <section class="mb-6">
                <div class="mt-4">
                <p><strong>Team, Arrify</strong></p>
                <p>Service Provider</p>
                <table style="width: 80%;">
                  <tbody>
                    <tr>
                        <td style="width: 7%; padding-top: 10px;vertical-align: bottom;">
                            Date : 
                        </td>
                        <td style=" width:23%;vertical-align: bottom;">
                            <strong v-if="contractData">{{ contractData.eSignatureWrapper.signDate }}</strong>
                        </td>
                        <td style="width: 5%;"></td>
                        <td style="width: 10%;padding-top: 10px;vertical-align: bottom;">
                            Signature : 
                        </td>
                        <td style="border-bottom: 2px dotted #333; width:30%;">
                            <img src="@/assets/arrify_signature.png" alt="signature" width="110px;" height="55px;">
                        </td>
                    </tr>
                  </tbody>
                </table>
                </div>
            </section>
            </div>
            <div class="pt-5 pb-5 button-container">
            <button v-on:click="submitForm" class="bg-blue-500 hover:bg-blue-700 text-white hover:text-blue-100 rounded">Submit</button>
            </div>
            &nbsp;
        </div>
    </client-only>
    <div v-if="!isLoading" class="header-footer">
        <FooterComponent />
    </div>
    </div>
    </template>
  
    <script setup>
    import { ref, onMounted, watch, computed } from 'vue';
    import axios from 'axios';
    import { useRoute, useRouter } from 'vue-router';
    import SignatureComponent from '../components/SignatureComponent.vue'
    import VueDatePicker from '@vuepic/vue-datepicker';
    import '@vuepic/vue-datepicker/dist/main.css';
    import { getEsignContractData,postEsignContractData } from '@/api/wordpress';
    import DemoForm from '../components/DemoForm.vue';
    import {useCommonHead} from '../composables/useCommonHead';
    import HeaderComponent from "../components/Header.vue";
    import FooterComponent from "../components/Footer.vue";
  
    const route = useRoute();
    const router = useRouter();
  
    const isLoading = ref(true);
    const showErrorMessage = ref(false);
    const showContent = ref(false);
    const hasContractId = ref(false);
    //const isSubmitting = ref(false);
    const errorMessage = ref('');
    const contractData = ref();
    const errorMessages = ref({});
  
    //Set up head using useCommonHead
    /*const pageTitle = 'E-signature Contract Demo';
    const pageDescription = 'Review and sign the Demo Contract Agreement between the Client and Service Provider.';
    const currentUrl = '';*/
  
    const pageTitle = computed(() => {
          return 'E-signature Contract Demo';
      });
  
      const pageDescription = computed(() => {
        if (hasContractId) return 'Review and sign the Demo Contract Agreement between the Client and Service Provider.';
          return 'Experience our E-Signature Contract Demo to streamline your document signing process and improve workflow efficiency.';
      });
  
      const currentUrl = computed(() => {
          return '';
      });
  
    // Initial call to set the head
    useCommonHead(pageTitle.value, pageDescription.value, currentUrl.value);
  
    const fetchData = async (contractId) => {
        try {
          
          //const baseUrl = 'https://arrify-connect-dev-ed.my.salesforce-sites.com/services/apexrest';
          //const endpointUrl = `${baseUrl}/demo/e-signature?id=${contractId}`;
          const endpointUrl = `/demo/e-signature?id=${contractId}`;
  
          /*axios.get(endpointUrl)
            .then(response => {
              console.log(response.status);
              if (response.status == 200) {
                setTimeout(() => {
                  isLoading.value = false;
                }, 2000);
  
                contractData.value = response.data.data;
                console.log('contractData');
                console.log(contractData);
                if(contractData.value.isSigned){
                  showContent.value = false;
                  showErrorMessage.value = true;
                  errorMessage.value = 'Contrct already submitted.';
                }else{
                  showContent.value = true;
                }
              } else { 
                  console.log('response.status : ' + response.status);
              }
            })
            .catch(response => {
              isLoading.value = false;
              console.log(response);
            });*/
  
          const response = await getEsignContractData(endpointUrl);
          console.log(response);
          if (response.success) {
            setTimeout(() => {
              isLoading.value = false;
            }, 2000);
  
            contractData.value = response.data;
            console.log('contractData');
            console.log(contractData);
            if(contractData.value.eSignatureWrapper.isSigned){
              showContent.value = false;
              showErrorMessage.value = true;
              errorMessage.value = 'Contrct already submitted.';
            }else{
              showContent.value = true;
            }
          } else { 
              console.log('response.success : ' + response.success);
          }
        } catch (error) {
          isLoading.value = false;
          showErrorMessage.value = true;
          errorMessage.value = 'The URL you provided is invalid or has expired.';
          console.error('Failed to fetch contract Data:', error);
        } 
    };
  
    const submitForm = async () => {
      console.log('submitForm');
      if (validateForm()) {
        isLoading.value = true;
        /*const baseUrl = 'https://arrify-connect-dev-ed.my.salesforce-sites.com/services/apexrest';
        const endpointUrl = `${baseUrl}/demo/e-signature`;*/
        const endpointUrl = `/demo/e-signature`;
        console.log('endpointUrl : ' + endpointUrl);
        try {
          /*const response = await axios.post(endpointUrl, {
            ...contractData.value
          });*/
          const response = await postEsignContractData(endpointUrl, contractData.value);
          if (response.success) {
            console.log(response);
            setTimeout(() => {
              isLoading.value = false;
            }, 2000);
            console.log('Form submitted successfully:');
            console.log(response.data);
            router.push({path: '/demo/e-signature-success', query: {id: contractData.value.eSignatureWrapper.contractId}});
          }else { 
            console.log('response.success : ' + response.success);
          }
        } catch (error) {
          isLoading.value = false;
          console.error('Error submitting form:');
          console.error(error);
        } 
      }else{
        console.log('validation failed ');
      }
    };
  
    const validateForm = () => {
      let errors = {};
      let isValid = true;
  
      console.log('contractData');
      console.log(contractData.value);
  
      if (!contractData.value.eSignatureWrapper.signDate) {
        errors.signDate = 'Sign date is required';
        isValid = false;
      }
      if (!contractData.value.eSignatureWrapper.place) {
        errors.place = 'Location is required';
        isValid = false;
      }
      if (!contractData.value.eSignatureWrapper.signData) {
        errors.signature = 'Signature is required';
        isValid = false;
      }
  
      console.log('errors');
      console.log(errors);
  
      errorMessages.value = errors;
  
      // If form is not valid, scroll to the first error
      if (!isValid) {
        setTimeout(() => {
          const firstError = document.querySelector('.error-message');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 0);
      }
  
      return isValid;
    };
  
    onMounted(() => {
      console.log('onMounted');
      const contractId = route.query.id;
      console.log('contractId : ' + contractId);
  
      if(contractId){
        hasContractId.value = true
        fetchData(contractId);
      }else{
        isLoading.value = false;
      }
      console.log('hasContractId : ' + hasContractId.value);
    });
  
    watch(() => route.query.id, (newId) => {
      console.log('watch');
      if (newId) {
        hasContractId.value = true;
        fetchData(newId);
      }
    });
  
    </script>
  
  <style scoped>
  
  #app, #app * {
    box-sizing: content-box;
  }
  
  input {
    background-color: #ffffd5; /* Light yellow background */
    border: none; /* Remove default border */
    border-bottom: 2px solid #333; /* Add bottom border */
    padding-top: 2px; /* Add some padding */
    padding-bottom: 2px; /* Add some padding */
    padding-right: 2px; /* Add some padding */
    outline: none; /* Remove outline on focus */
    border-radius: 0px;
    margin: 5px 0px;
  }
  
  input:focus {
    background-color: #ffffe0; /* Slightly darker yellow on focus */
  }
  
  .error-message {
    position: absolute;
    background-color: rgb(218, 56, 56);
    color:white;
    padding: 5px 10px;
    border-radius: 3px;
    font-size: 0.8em;
    z-index: 500;
    white-space: nowrap;
    pointer-events: none; /* Allows clicking through the error message */
  }
  
  .error-message {
    animation: fadeIn 0.3s ease-in-out;
  }
  
  .page {
    width: 21cm;
    min-height: 29.7cm;
    padding-top: 2cm;
    padding-bottom: 2cm;
    padding-right: 3cm;
    padding-left: 2.5cm;
    margin: 1cm auto;
    border: 1px solid #d3d3d3;
    border-radius: 0px;
    background-color: white;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
    page-break-after: always;
    position: relative;
    font-family: Arial, sans-serif;
    font-size:16px;
  }
  
  @media screen and (max-width: 27cm) {
    .page {
      margin-left: 0;
      margin-right: 0;
      border: none;
      box-shadow: none;
    }
  }
  
  .header-footer{
    width: 100%;
  }
  
  .button-container {
    width: 26.5cm;
    margin: 1cm auto;
    border: 1px solid #d3d3d3;
    text-align: right;
    border-radius: 0px;
    background-color: white;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
    position: relative;
    font-family: Arial, sans-serif;
    font-size:16px;
  }
  
  button {
    padding: 12px 24px;
    margin-right: 48px;
  }
  
  </style> 